import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { APP_NAME } from '@a8-planner/shared/constants/app';
import { ErrorBoundary } from './features/errors/ErrorBoundary';
import { ErrorFallback } from './features/errors/ErrorFallback';
import './styles/globals.css';
// i18n MUST initialize before the first render — the login screen is
// translated and bootstraps its language pre-auth (Batch B).
import './lib/i18n';

// Import route tree
import { routeTree } from './routeTree.gen';

// Create the router instance. `defaultErrorComponent` renders a recoverable UI
// (with a route-scoped `reset`) for any error thrown inside a route component or
// loader — the common case — instead of TanStack Router's bare default.
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, reset }) => <ErrorFallback error={error} onRetry={reset} />,
});

// Register the router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// index.html's <title> is only the pre-JS fallback; the constant wins here
// so a rebrand is a one-line change in packages/shared.
document.title = APP_NAME;

// One-time cleanup (Phase 10): scrub the retired localStorage token bridge.
// Auth is now cookie-only, but users logged in before the retirement still
// carry a JS-readable `a8planner_access_token` in localStorage — the exact XSS
// surface this shipped to remove — and the backend still accepts Bearer tokens
// (apps/api/src/middleware/auth.ts), so a stale unexpired copy remains a usable
// credential if stolen. removeItem is a no-op when the key is absent.
// Safe to delete once all active sessions have loaded the app once post-deploy.
localStorage.removeItem('a8planner_access_token');

const root = document.getElementById('root');
if (root) {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      {/* Outer backstop: catches render errors ABOVE the route tree (providers,
          the router instance itself) that defaultErrorComponent can't. */}
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </React.StrictMode>,
  );
}
